import type { JournalActionDetail, RevertTarget } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { dateTime } from '../../../lib/format';
import { Button } from '../../../ui/button';
import { Sheet } from '../../../ui/dialog';
import { Badge, ErrorBox, Spinner } from '../../../ui/feedback';
import { KeyValue } from '../../../ui/layout';
import {
  ActionStateBadge,
  actorName,
  ChangeRows,
  CountBadges,
  methodWord,
  OP_WORD,
  routeText,
} from './shared';

/** Одно действие сотрудника целиком: все записи, которые оно изменило, и отмена всего действия */
export function ActionSheet({
  actionKey,
  canRevert,
  onClose,
  onRecord,
  onRevert,
}: {
  actionKey: string;
  canRevert: boolean;
  onClose: () => void;
  onRecord: (record: string) => void;
  onRevert: (target: RevertTarget, title: string) => void;
}) {
  const q = useQuery({
    queryKey: ['journal', 'action', actionKey],
    queryFn: () => api<JournalActionDetail>(`/admin/journal/actions/${encodeURIComponent(actionKey)}`),
  });
  const a = q.data;
  return (
    <Sheet
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="Действие"
      description={a ? a.title : undefined}
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : a ? (
        <div className="flex flex-col gap-5">
          <KeyValue
            items={[
              {
                label: 'Когда',
                value: (
                  <span className="num">
                    {dateTime(a.at)}
                    {a.startedAt.slice(0, 16) !== a.at.slice(0, 16)
                      ? ` (начато ${dateTime(a.startedAt)})`
                      : ''}
                  </span>
                ),
              },
              { label: 'Кто', value: actorName(a.actor) },
              {
                label: 'Откуда',
                value: [a.device, a.ip].filter(Boolean).join(' · ') || '—',
              },
              {
                label: 'Что вызвано',
                value: a.route ? (
                  <span className="[overflow-wrap:anywhere]">
                    {methodWord(a.method)} · <span className="text-ink-2">{routeText(a.route)}</span>
                  </span>
                ) : (
                  '—'
                ),
              },
            ]}
          />
          <div className="flex flex-wrap gap-1.5">
            <CountBadges counts={a.counts} />
            <ActionStateBadge a={a} />
          </div>

          {a.revertedBy ? (
            <p className="rounded-md bg-warning-soft px-3 py-2.5 text-[13px] text-warning">
              Отменено: {actorName(a.revertedBy.actor)},{' '}
              <span className="num">{dateTime(a.revertedBy.at)}</span>. Причина: {a.revertedBy.reason}
            </p>
          ) : null}
          {a.revertOf ? (
            <p className="rounded-md bg-info-soft px-3 py-2.5 text-[13px] text-info">
              Это отмена других изменений. Причина: {a.revertOf.reason}
              {a.revertOf.undone ? '. Позже эту отмену отменили — изменения снова действуют.' : ''}
            </p>
          ) : null}

          {canRevert ? (
            <div className="flex flex-wrap gap-2">
              {a.revertOf && !a.revertOf.undone ? (
                <Button
                  variant="danger"
                  icon={<Undo2 className="size-4" aria-hidden />}
                  onClick={() => onRevert({ kind: 'undo', revertId: a.revertOf!.id }, 'Отменить отмену')}
                >
                  Отменить эту отмену
                </Button>
              ) : null}
              {!a.revertOf && a.revertable ? (
                <Button
                  variant="danger"
                  icon={<Undo2 className="size-4" aria-hidden />}
                  onClick={() => onRevert({ kind: 'request', keys: [a.key] }, 'Отменить действие')}
                >
                  Отменить всё действие
                </Button>
              ) : null}
            </div>
          ) : null}

          <section className="flex flex-col gap-3">
            <h3 className="text-[14px] font-semibold text-ink">
              Записи ({a.total}
              {a.truncated ? ', показаны первые 500' : ''})
            </h3>
            <ol className="flex flex-col gap-4">
              {a.entries.map((e) => (
                <li key={e.id} className="text-[14px]">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-ink">
                      <b className="font-semibold">{OP_WORD[e.op]}</b> · {e.tableTitle}
                      {e.recordLabel ? (
                        <span className="[overflow-wrap:anywhere]"> {e.recordLabel}</span>
                      ) : null}
                    </span>
                    {e.reverted ? <Badge tone="warning">отменено</Badge> : null}
                  </div>
                  <ChangeRows op={e.op} table={e.table} changes={e.changes} />
                  {e.rowId ? (
                    <button
                      type="button"
                      onClick={() => onRecord(`${e.schema}.${e.table}:${e.rowId}`)}
                      className="mt-1 inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline underline-offset-2 md:min-h-0"
                    >
                      История записи и состояние на дату
                    </button>
                  ) : null}
                </li>
              ))}
            </ol>
          </section>
        </div>
      ) : null}
    </Sheet>
  );
}
